
import { useState } from 'react';
import {
  nettoyageExpress,
  nettoyageApprofondi,
  nettoyageSupreme,
  polishCorrection,
  ceramiquePacksComplets,
  ceramiqueIndividuel,
  cielEtoile,
} from '../../../mocks/prestations';

interface PrestationStepProps {
  data: any;
  updateData: (data: any) => void;
  nextStep: () => void;
  prevStep: () => void;
}

export default function PrestationStep({
  data,
  updateData,
  nextStep,
  prevStep,
}: PrestationStepProps) {
  const [ceramiqueTab, setCeramiqueTab] = useState<'packs' | 'individuel'>('packs');

  const isCeramique = data.category === 'ceramique';

  const categories: Record<
    string,
    { name: string; prestations: any[] }
  > = {
    express: { name: 'Express', prestations: nettoyageExpress },
    approfondi: { name: 'Approfondi', prestations: nettoyageApprofondi },
    supreme: { name: 'Suprême', prestations: nettoyageSupreme },
    polish: { name: 'Polish & Correction', prestations: polishCorrection },
    ciel: { name: 'Ciel Étoilé', prestations: cielEtoile },
  };

  const selectedCategory = isCeramique
    ? {
        name: 'Céramique',
        prestations:
          ceramiqueTab === 'packs'
            ? ceramiquePacksComplets
            : ceramiqueIndividuel,
      }
    : categories[data.category];

  const handleSelectPrestation = (
    prest: any,
    size: string,
    price: number
  ) => {
    updateData({
      prestation: prest,
      size,
      price,
    });
  };

  const canContinue =
    data.prestation && data.size && data.price > 0;

  const renderPrestationCard = (prest: any) => {
    const hasPrices = prest.prices && prest.prices.length > 0;
    const hasFixedPrice = prest.price && !prest.prices;
    const badge = (prest as any).badge;

    return (
      <div
        key={prest.id}
        className="border border-dark-border rounded-xl p-6 hover:border-primary/50 transition-all duration-300 relative overflow-hidden"
      >
        {badge && (
          <div className="absolute top-0 right-0 bg-primary text-dark-bg text-xs font-bold px-3 py-1 rounded-bl-lg">
            {badge}
          </div>
        )}

        <h3 className="text-lg font-bold text-white mb-2">{prest.name}</h3>
        <p className="text-gray-400 text-sm mb-4 leading-relaxed">
          {prest.description}
        </p>

        {hasPrices && (
          <div className="grid grid-cols-2 gap-3">
            {prest.prices.map((priceItem: any) => (
              <button
                key={`${prest.id}-${priceItem.size}`}
                onClick={() =>
                  handleSelectPrestation(
                    prest,
                    priceItem.size,
                    priceItem.price
                  )
                }
                className={`p-4 rounded-lg border-2 transition-all duration-300 cursor-pointer hover:scale-105 ${
                  data.prestation?.id === prest.id &&
                  data.size === priceItem.size
                    ? 'border-primary bg-primary/10'
                    : 'border-dark-border hover:border-primary/50'
                }`}
              >
                <div className="text-center">
                  <div className="text-sm text-gray-400 mb-1">
                    {priceItem.size}
                  </div>
                  <div className="text-xl font-bold text-primary">
                    {priceItem.price} CHF
                  </div>
                  {priceItem.duration && (
                    <div className="text-xs text-gray-500 mt-1">
                      {priceItem.duration}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {hasFixedPrice && (
          <button
            onClick={() =>
              handleSelectPrestation(prest, 'Standard', prest.price)
            }
            className={`w-full p-4 rounded-lg border-2 transition-all duration-300 cursor-pointer hover:scale-105 ${
              data.prestation?.id === prest.id
                ? 'border-primary bg-primary/10'
                : 'border-dark-border hover:border-primary/50'
            }`}
          >
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {prest.price} CHF
              </div>
              {prest.duration && (
                <div className="text-xs text-gray-500 mt-1">
                  {prest.duration}
                </div>
              )}
            </div>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="bg-dark-card border border-dark-border rounded-2xl p-8">
      <button
        onClick={prevStep}
        className="flex items-center text-primary hover:text-primary/80 transition-colors duration-300 mb-4 cursor-pointer"
      >
        <i className="ri-arrow-left-line mr-2"></i>
        Retour
      </button>

      <h2 className="text-2xl font-bold text-white mb-2">
        {selectedCategory?.name}
      </h2>

      {isCeramique && (
        <>
          <p className="text-gray-400 text-sm mb-6">
            Choisissez un pack complet ou composez votre service à la carte
          </p>

          <div className="flex bg-dark-bg rounded-full p-1 mb-8">
            <button
              onClick={() => {
                setCeramiqueTab('packs');
                updateData({ prestation: null, size: '', price: 0 });
              }}
              className={`flex-1 py-3 px-4 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                ceramiqueTab === 'packs'
                  ? 'bg-primary text-dark-bg'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <i className="ri-gift-line mr-2"></i>
              Packs Complets
            </button>
            <button
              onClick={() => {
                setCeramiqueTab('individuel');
                updateData({ prestation: null, size: '', price: 0 });
              }}
              className={`flex-1 py-3 px-4 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                ceramiqueTab === 'individuel'
                  ? 'bg-primary text-dark-bg'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <i className="ri-list-check mr-2"></i>
              À la carte
            </button>
          </div>

          {ceramiqueTab === 'packs' && (
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-6 flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center shrink-0">
                <i className="ri-star-line text-primary text-lg"></i>
              </div>
              <div>
                <p className="text-white text-sm font-semibold mb-1">
                  Meilleur rapport qualité-prix
                </p>
                <p className="text-gray-400 text-xs leading-relaxed">
                  Les packs complets incluent la céramique carrosserie + vitres
                  + 4 jantes à un tarif avantageux.
                </p>
              </div>
            </div>
          )}

          {ceramiqueTab === 'individuel' && (
            <div className="bg-dark-bg border border-dark-border rounded-xl p-4 mb-6 flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center shrink-0">
                <i className="ri-settings-3-line text-gray-400 text-lg"></i>
              </div>
              <div>
                <p className="text-white text-sm font-semibold mb-1">
                  Services individuels
                </p>
                <p className="text-gray-400 text-xs leading-relaxed">
                  Sélectionnez uniquement le service dont vous avez besoin :
                  carrosserie, vitres, jantes, cuir ou plastiques.
                </p>
              </div>
            </div>
          )}
        </>
      )}

      {!isCeramique && <div className="mb-6" />}

      <div className="space-y-4 mb-6">
        {selectedCategory?.prestations.map((prest: any) =>
          renderPrestationCard(prest)
        )}
      </div>

      <button
        onClick={nextStep}
        disabled={!canContinue}
        className="w-full py-4 bg-primary text-dark-bg rounded-xl font-bold text-lg hover:bg-primary/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
      >
        Continuer →
      </button>
    </div>
  );
}
